import React, { useState, useEffect } from 'react';
import { 
  COOKIE_PREFERENCES_KEY, 
  COOKIE_CONSENT_KEY, 
  DEFAULT_PREFERENCES,
  dispatchCookiePreferenceChange
} from '../utils/cookieManager';
import { initializeAnalytics, removeAnalytics } from '../utils/analytics';
import './CookiePreferencesModal.css';

function CookiePreferencesModal({ isOpen, onClose, onSave }) {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  useEffect(() => {
    if (isOpen) {
      // Load existing preferences when modal opens
      const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPreferences) {
        try {
          const parsed = JSON.parse(savedPreferences);
          setPreferences({ ...DEFAULT_PREFERENCES, ...parsed });
        } catch (e) {
          console.warn('Invalid cookie preferences format, using defaults');
          setPreferences(DEFAULT_PREFERENCES);
        }
      }
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handlePreferenceChange = (category, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleSave = async () => {
    // Save preferences to localStorage
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    
    // Notify components of the preference change
    dispatchCookiePreferenceChange();
    
    // Handle analytics based on preferences
    try {
      if (preferences.analytics) {
        await initializeAnalytics();
        console.log('Analytics initialized based on user preferences');
      } else {
        removeAnalytics();
        console.log('Analytics disabled based on user preferences');
      }
    } catch (error) {
      console.error('Error handling analytics preferences:', error);
    }
    
    // Call parent callback
    if (onSave) {
      onSave(preferences);
    }
    
    onClose();
  };

  const handleAcceptAll = async () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      personalization: true
    };
    
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAccepted));
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    
    // Notify components of the preference change
    dispatchCookiePreferenceChange();
    
    // Initialize analytics since user accepted all
    try {
      await initializeAnalytics();
      console.log('Analytics initialized - user accepted all cookies');
    } catch (error) {
      console.error('Error initializing analytics:', error);
    }
    
    if (onSave) {
      onSave(allAccepted);
    }
    
    onClose();
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      marketing: false,
      personalization: false
    };
    
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(onlyEssential));
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    
    // Notify components of the preference change
    dispatchCookiePreferenceChange();
    
    if (onSave) {
      onSave(onlyEssential);
    }
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="cookie-modal-overlay" onClick={onClose}>
      <div className="cookie-modal" onClick={e => e.stopPropagation()}>
        <div className="cookie-modal-header">
          <h2>Cookie Preferences</h2>
          <button 
            className="cookie-modal-close" 
            onClick={onClose}
            aria-label="Close cookie preferences"
          >
            ×
          </button>
        </div>
        
        <div className="cookie-modal-content">
          <p className="cookie-modal-description">
            We use cookies to improve your browsing experience and provide personalized content. 
            You can choose which categories of cookies you want to allow.
          </p>
          
          <div className="cookie-categories">
            {/* Essential Cookies - Always enabled */}
            <div className="cookie-category">
              <div className="cookie-category-header">
                <div className="cookie-category-info">
                  <h3>Essential Cookies</h3>
                  <span className="cookie-status essential-status">Always Active</span>
                </div>
                <input
                  type="checkbox"
                  checked={true}
                  disabled={true}
                  className="cookie-toggle essential-toggle"
                />
              </div>
              <p className="cookie-category-description">
                These cookies are necessary for the website to function and cannot be disabled. 
                They enable core functionality including:
                <br />• Authentication and login sessions
                <br />• Shopping cart and favorites
                <br />• Security protection (CSRF tokens)
                <br />• Cookie consent preferences
                <br />• Site navigation and routing
                <br />• Accessibility features
                <br />• Error handling and site stability
              </p>
            </div>

            {/* Analytics Cookies */}
            <div className="cookie-category">
              <div className="cookie-category-header">
                <div className="cookie-category-info">
                  <h3>Analytics Cookies</h3>
                  <span className="cookie-count">Google Analytics, Performance Tracking</span>
                </div>
                <label className="cookie-toggle-wrapper">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                    className="cookie-toggle"
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <p className="cookie-category-description">
                These cookies help us understand how visitors interact with our website by 
                collecting and reporting information anonymously.
              </p>
            </div>

            {/* Marketing Cookies */}
            <div className="cookie-category">
              <div className="cookie-category-header">
                <div className="cookie-category-info">
                  <h3>Marketing Cookies</h3>
                  <span className="cookie-count">Advertising, Social Media</span>
                </div>
                <label className="cookie-toggle-wrapper">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                    className="cookie-toggle"
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <p className="cookie-category-description">
                These cookies track your activity across websites to deliver more relevant 
                advertising and measure campaign effectiveness.
              </p>
            </div>

            {/* Personalization Cookies */}
            <div className="cookie-category">
              <div className="cookie-category-header">
                <div className="cookie-category-info">
                  <h3>Personalization Cookies</h3>
                  <span className="cookie-count">User Preferences, Language Settings</span>
                </div>
                <label className="cookie-toggle-wrapper">
                  <input
                    type="checkbox"
                    checked={preferences.personalization}
                    onChange={(e) => handlePreferenceChange('personalization', e.target.checked)}
                    className="cookie-toggle"
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <p className="cookie-category-description">
                These cookies remember your preferences and settings to provide a more 
                personalized experience on future visits.
              </p>
            </div>
          </div>
        </div>
        
        <div className="cookie-modal-footer">
          <div className="cookie-modal-actions">
            <button 
              className="cookie-button secondary" 
              onClick={handleRejectAll}
            >
              Reject All
            </button>
            <button 
              className="cookie-button primary" 
              onClick={handleSave}
            >
              Save Preferences
            </button>
            <button 
              className="cookie-button accent" 
              onClick={handleAcceptAll}
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookiePreferencesModal;
